import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import DashboardLayout from '@/components/DashboardLayout'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface Teacher {
  id: number
  name: string
}

interface Student {
  id: number
  name: string
}

interface Lesson {
  id: number
  lesson_date: string
  start_time: string
  end_time: string
  student: Student
  students_count: number
  unit_teacher_payment: number
  teacher_payment: number
  description: string | null
}

interface Payment {
  id: number
  teacher: Teacher
  total_amount: number
  formatted_total: string
  status: string
  payment_date: string | null
  notes: string | null
  created_at: string
}

interface Props {
  payment: Payment
  lessons: Lesson[]
}

const statusConfig = {
  pending: { label: 'Pendente', variant: 'secondary' as const },
  paid: { label: 'Pago', variant: 'default' as const },
  cancelled: { label: 'Cancelado', variant: 'destructive' as const },
}

export default function TeacherPaymentsShow({ payment, lessons }: Props) {
  const [showMarkAsPaidDialog, setShowMarkAsPaidDialog] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [paymentDate, setPaymentDate] = useState(
    payment.payment_date || format(new Date(), 'yyyy-MM-dd')
  )

  const handleMarkAsPaid = () => {
    router.patch(
      `/teacher_payments/${payment.id}`,
      {
        action_type: 'mark_as_paid',
        payment_date: paymentDate,
      },
      {
        onSuccess: () => setShowMarkAsPaidDialog(false),
      }
    )
  }

  const handleCancel = () => {
    router.patch(
      `/teacher_payments/${payment.id}`,
      { action_type: 'cancel' },
      {
        onSuccess: () => setShowCancelDialog(false),
      }
    )
  }

  return (
    <DashboardLayout>
      <Head title={`Pagamento - ${payment.teacher.name}`} />

      <div className="p-8">
        <div className="flex items-center gap-4 mb-6">
          <Button asChild variant="outline">
            <Link href="/teacher_payments">← Voltar</Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Detalhes do Pagamento</h1>
            <p className="text-muted-foreground mt-1">
              Pagamento #{payment.id} - {payment.teacher.name}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent>
              <Badge variant={statusConfig[payment.status as keyof typeof statusConfig].variant} className="text-lg px-4 py-1">
                {statusConfig[payment.status as keyof typeof statusConfig].label}
              </Badge>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Valor Total</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{payment.formatted_total}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {lessons.length} {lessons.length === 1 ? 'aula' : 'aulas'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Data de Pagamento</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">
                {payment.payment_date
                  ? format(new Date(payment.payment_date), 'dd/MM/yyyy', { locale: ptBR })
                  : '-'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Criado em {format(new Date(payment.created_at), 'dd/MM/yyyy', { locale: ptBR })}
              </p>
            </CardContent>
          </Card>
        </div>

        {payment.notes && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Observações</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{payment.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Ações */}
        {payment.status === 'pending' && (
          <div className="flex gap-4 mb-6">
            <Button onClick={() => setShowMarkAsPaidDialog(true)}>
              Marcar como Pago
            </Button>
            <Button variant="destructive" onClick={() => setShowCancelDialog(true)}>
              Cancelar Pagamento
            </Button>
          </div>
        )}

        {/* Lista de Aulas */}
        <Card>
          <CardHeader>
            <CardTitle>Aulas Incluídas ({lessons.length})</CardTitle>
            <CardDescription>
              Todas as aulas que fazem parte deste pagamento
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Horário</TableHead>
                    <TableHead>Aluno</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Observações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lessons.map((lesson) => (
                    <TableRow key={lesson.id}>
                      <TableCell>
                        {format(new Date(lesson.lesson_date), 'dd/MM/yyyy', { locale: ptBR })}
                      </TableCell>
                      <TableCell>
                        {lesson.start_time} - {lesson.end_time}
                      </TableCell>
                      <TableCell>
                        {lesson.student ? lesson.student.name : ''}
                        {lesson.students_count > 1 && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            +{lesson.students_count - 1} aluno(s)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-semibold">
                        R$ {lesson.teacher_payment.toFixed(2)}
                        {lesson.students_count > 1 && (
                          <span className="block text-xs font-normal text-muted-foreground">
                            R$ {lesson.unit_teacher_payment.toFixed(2)} × {lesson.students_count}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {lesson.description || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog - Marcar como Pago */}
      <AlertDialog open={showMarkAsPaidDialog} onOpenChange={setShowMarkAsPaidDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar como Pago</AlertDialogTitle>
            <AlertDialogDescription>
              Confirme a data do pagamento para o professor {payment.teacher.name}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="payment_date">Data do Pagamento</Label>
            <Input
              id="payment_date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkAsPaid}>
              Confirmar Pagamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog - Cancelar */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Pagamento</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja cancelar este pagamento? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Sim, Cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  )
}
